/**
 * Colour Swatch Handler
 * Handles dynamic colour swatch rendering for product variants
 * @version 1.0.0
 */

/**
 * Parse color swatch configuration from theme settings
 * @returns {Object} Color map from settings
 */
function parseColorSwatchConfig() {
  const defaultColors = {
    'BLACK': '#000000',
    'WHITE': '#FFFFFF',
    'CYAN BLUE': '#00BCD4',
    'PULSE BLUE': '#007BFF',
    'RED': '#FF0000',
    'NAVY': '#001F3F',
    'GREY': '#808080',
    'YELLOW': '#FFEB3B',
    'GREEN': '#4CAF50',
    'PURPLE': '#9C27B0',
    'ORANGE': '#FF9800',
    'PINK': '#E91E63',
    'BLACK/CYAN BLUE': 'linear-gradient(135deg, #000000 0%, #000000 49.9%, #1997b3 50%, #1997b3 100%)',
    'BLACK/PULSE BLUE': 'linear-gradient(135deg, #000000 0%, #000000 49.9%, #4dabce 50%, #4dabce 100%)',
    'NAVY/HOT PINK': 'linear-gradient(135deg, #001f5b 0%, #001f5b 49.9%, #ff69b4 50%, #ff69b4 100%)',
    'HOT PINK/NAVY': 'linear-gradient(135deg, #ff69b4 0%, #ff69b4 49.9%, #001f5b 50%, #001f5b 100%)',
    'AMPARO BLUE/PULSE BLUE': 'linear-gradient(135deg, #445798 0%, #445798 49.9%, #4dabce 50%, #4dabce 100%)',
    'BLACK/BLUE GREEN': 'linear-gradient(135deg, #000000 0%, #000000 49.9%, #009b9e 50%, #009b9e 100%)'
  };

  // Check if window.themeSettings exists and has colorSwatchConfig
  if (typeof window === 'undefined' || !window.themeSettings || !window.themeSettings.colorSwatchConfig) {
    return defaultColors;
  }

  const config = window.themeSettings.colorSwatchConfig;

  // If config is empty or not a string, return defaults
  if (typeof config !== 'string' || !config.trim()) {
    return defaultColors;
  }

  const colorMap = { ...defaultColors };

  try {
    // Parse the textarea config (format: COLOR_NAME = value)
    const lines = config.split('\n');
    lines.forEach(line => {
      line = line.trim();

      // Skip empty lines and comments
      if (!line || line.startsWith('#') || line.startsWith('//')) return;

      // Check if line contains '='
      if (!line.includes('=')) return;

      // Split only on the first '=' to allow '=' in gradient values
      const equalsIndex = line.indexOf('=');
      const colorName = line.substring(0, equalsIndex).trim();
      const colorValue = line.substring(equalsIndex + 1).trim();

      if (colorName && colorValue) {
        colorMap[colorName.toUpperCase()] = colorValue;
      }
    });
  } catch (error) {
    console.error('Error parsing color swatch config:', error);
    return defaultColors;
  }

  return colorMap;
}

if (!customElements.get('colour-swatch-handler')) {
  class ColourSwatchHandler extends HTMLElement {
    constructor() {
      super();
      this.colorMap = parseColorSwatchConfig();
    }

    connectedCallback() {
      this.initializeSwatches();
    }

    /**
     * Initialize all colour swatches within this component
     */
    initializeSwatches() {
      const swatches = this.querySelectorAll('.colour-swatch');
      swatches.forEach((swatch) => this.applySwatchColor(swatch));
    }

    /**
     * Apply colour or gradient to a swatch element
     * @param {HTMLElement} swatch - The swatch element to style
     */
    applySwatchColor(swatch) {
      const rawColor = swatch.dataset.color;
      if (!rawColor) return;

      const gradient = this.getColorGradient(rawColor);
      swatch.style.background = gradient;
      swatch.style.setProperty('--swatch-color', gradient);
    }

    /**
     * Get colour value or gradient for a given colour name
     * @param {string} colorName - The colour name from the variant
     * @returns {string} CSS colour value or gradient
     */
    getColorGradient(colorName) {
      const cleanName = colorName.trim().toUpperCase();

      // Return pre-defined colour mapping
      if (this.colorMap[cleanName]) {
        return this.colorMap[cleanName];
      }

      // Auto-detect multi-colour variants (e.g., "BLACK/CYAN BLUE")
      const parts = cleanName.split(/[\/;&,-]+/).map(p => p.trim()).filter(Boolean);

      if (parts.length > 1) {
        const gradientColors = parts
          .map(part => this.colorMap[part] || part.toLowerCase())
          .join(', ');
        return `linear-gradient(135deg, ${gradientColors})`;
      }

      // Fallback: return colour name as-is (for CSS named colours or custom values)
      return colorName.toLowerCase();
    }
  }

  customElements.define('colour-swatch-handler', ColourSwatchHandler);
}

/**
 * Initialize colour swatches on page load
 * Handles legacy implementations without custom element wrapper
 */
(function initializeColourSwatches() {
  'use strict';

  // Use the same parsed config as the custom element
  const COLOR_MAP = parseColorSwatchConfig();

  /**
   * Get colour value or gradient for a given colour name
   * @param {string} colorName - The colour name from the variant
   * @returns {string} CSS colour value or gradient
   */
  function getColorGradient(colorName) {
    const cleanName = colorName.trim().toUpperCase();

    // Return pre-defined colour mapping
    if (COLOR_MAP[cleanName]) {
      return COLOR_MAP[cleanName];
    }

    // Auto-detect multi-colour variants (e.g., "BLACK/CYAN BLUE")
    const parts = cleanName.split(/[\/;&,-]+/).map(p => p.trim()).filter(Boolean);

    if (parts.length > 1) {
      const gradientColors = parts
        .map(part => COLOR_MAP[part] || part.toLowerCase())
        .join(', ');
      return `linear-gradient(135deg, #000000 0%, ${gradientColors} 49.9%, ${gradientColors} 50%, ${gradientColors} 100%)`;
    }

    // Fallback: return colour name as-is (for CSS named colours or custom values)
    return colorName.toLowerCase();
  }

  /**
   * Apply colour to a single swatch element
   * @param {HTMLElement} swatch - The swatch element to style
   */
  function applySwatchColor(swatch) {
    const rawColor = swatch.dataset.color;
    if (!rawColor) return;

    const gradient = getColorGradient(rawColor);
    swatch.style.background = gradient;
    swatch.style.setProperty('--swatch-color', gradient);

    // Ensure disabled state is properly reflected
    const input = document.getElementById(swatch.getAttribute('for'));
    if (input && (input.disabled || input.classList.contains('disabled'))) {
      swatch.classList.add('disabled-swatch');
    }
  }

  /**
   * Initialize all colour swatches on the page
   */
  function initSwatches() {
    const swatches = document.querySelectorAll('.colour-swatch:not([data-initialized])');
    swatches.forEach((swatch) => {
      applySwatchColor(swatch);
      swatch.dataset.initialized = 'true';
    });
  }

  // Initialize on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSwatches);
  } else {
    // DOM already loaded
    initSwatches();
  }

  // Re-initialize on Shopify section render (for Theme Editor)
  document.addEventListener('shopify:section:load', initSwatches);
  document.addEventListener('shopify:section:reorder', initSwatches);

  // Handle variant changes and dynamically added swatches
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) { // Element node
          // Check if the node itself is a colour swatch
          if (node.classList && node.classList.contains('colour-swatch') && !node.dataset.initialized) {
            applySwatchColor(node);
            node.dataset.initialized = 'true';
          }
          // Check for colour swatches within the added node
          const swatches = node.querySelectorAll ? node.querySelectorAll('.colour-swatch:not([data-initialized])') : [];
          swatches.forEach((swatch) => {
            applySwatchColor(swatch);
            swatch.dataset.initialized = 'true';
          });
        }
      });
    });
  });

  // Observe the document for changes
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  // Expose public API for dynamic initialization
  window.ColourSwatchAPI = {
    init: initSwatches,
    applyColor: applySwatchColor,
    getColorGradient: getColorGradient,
  };
})();